/**
 * PÁGINA DE DETALLE DE NOTICIA
 * ================================
 * Muestra el contenido completo de una noticia
 */

import React, { useState, useEffect } from 'react';
import { Calendar, ArrowLeft, Loader2, User, Newspaper } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import ModernHeader from '../components/modern/ModernHeader';
import ModernFooter from '../components/modern/ModernFooter';
import { api } from '../services/api';

// URL base para las imágenes de portada de noticias
const PORTADA_BASE_URL = 'https://plazarural.com.uy/uploads/noticias/';

const NoticiaDetallePage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [scrollY, setScrollY] = useState(0);
    const [noticia, setNoticia] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Manejar scroll para header
    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Cargar noticia desde la API
    useEffect(() => {
        const fetchNoticia = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await api.getNoticiaDetalle(slug);
                console.log('[NoticiaDetallePage] Noticia cargada:', data);
                setNoticia(data);
            } catch (error) {
                console.error('[NoticiaDetallePage] Error cargando noticia:', error);
                setError('No se pudo cargar la noticia');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchNoticia();
        }
    }, [slug]);

    const portadaUrl = noticia?.imagen
        ? `${PORTADA_BASE_URL}${noticia.imagen}`
        : null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
            <ModernHeader scrollY={100} />

            <div className="pt-24 pb-12">
                <div className="max-w-4xl mx-auto px-4">

                    {/* Botón volver */}
                    <button
                        onClick={() => navigate('/noticias')}
                        className="flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold mb-6 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Volver a Noticias
                    </button>

                    {/* Loading state */}
                    {loading && (
                        <div className="flex items-center justify-center py-16">
                            <div className="text-center">
                                <Loader2 className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
                                <p className="text-gray-600 font-medium">Cargando noticia...</p>
                            </div>
                        </div>
                    )}

                    {/* Error state */}
                    {error && !loading && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                            <Newspaper className="w-16 h-16 text-red-300 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-red-700 mb-2">{error}</h2>
                            <button
                                onClick={() => navigate('/noticias')}
                                className="text-green-600 hover:underline font-semibold"
                            >
                                Ver todas las noticias
                            </button>
                        </div>
                    )}

                    {/* Contenido de la noticia */}
                    {!loading && noticia && (
                        <article className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">

                            {/* Imagen de portada */}
                            {portadaUrl && (
                                <div className="relative h-64 md:h-96 bg-gradient-to-br from-green-100 to-green-200">
                                    <img
                                        src={portadaUrl}
                                        alt={noticia.titulo}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}

                            {/* Contenido */}
                            <div className="p-6 md:p-10">

                                {/* Meta info */}
                                <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm mb-6">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-green-600" />
                                        <span>{noticia.fecha}</span>
                                    </div>
                                    {noticia.autor && (
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-green-600" />
                                            <span>{noticia.autor}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Título */}
                                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                    {noticia.titulo}
                                </h1>

                                {/* Subtítulo */}
                                {noticia.subtitulo && (
                                    <p className="text-xl text-gray-600 mb-8 border-l-4 border-green-500 pl-4">
                                        {noticia.subtitulo}
                                    </p>
                                )}

                                {/* Contenido HTML */}
                                <div
                                    className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-green-600 prose-img:rounded-xl"
                                    dangerouslySetInnerHTML={{ __html: noticia.contenido }}
                                />

                            </div>
                        </article>
                    )}

                </div>
            </div>

            <ModernFooter />
        </div>
    );
};

export default NoticiaDetallePage;
